/**
 * Displays a confirmation modal with customized styling and navigation functionality
 *
 * @param {string} projectDataId - The ID of the project to navigate to
 * @param {string} themeColor - The color to be used for styling the modal buttons
 * @param {Function} onConfirm - Optional callback to handle confirmation
 * @returns {Object} Object containing the displayModalConfirm function
 *
 *  * @example
 * ```tsx
 * const { displayModalConfirm } = useDisplayModalConfirm();
 * displayModalConfirm("projectId", "#FF0000", () => handleConfirm());
 * ```
 */

import { Modal } from "antd";
import { useNavigation, useTranslate } from "@refinedev/core";

export const useDisplayModalConfirm = () => {
  const { show } = useNavigation();
  const t = useTranslate();

  const displayModalConfirm = (
    projectDataId: string,
    themeColor: string,
    onConfirm?: () => void,
    onCancel?: () => void
  ) => {
    Modal.confirm({
      content: t(`components.binder.index.unsavedChanges`),
      onOk: () => {
        if (onConfirm) {
          onConfirm();
        } else {
          show("media_projects", projectDataId);
        }
      },
      onCancel: () => {
        if (onCancel) {
          onCancel();
        }
      },
      okText: t(`components.ConfirmModal.ConfirmModal.yes`),
      cancelText: t(`components.ConfirmModal.ConfirmModal.no`),
      okButtonProps: {
        style: {
          backgroundColor: themeColor,
        },
      },
      cancelButtonProps: {
        style: {
          borderColor: themeColor,
          color: themeColor,
        },
      },
    });
  };

  return { displayModalConfirm };
};
